import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Send, Gift, Info } from "lucide-react";
import { api, fmtError } from "@/lib/api";
import { fmtDate } from "@/constants/labels";

const STATUT_LABEL = { invite: "Invité", inscrit: "Inscrit", abonne: "Abonné" };
const STATUT_BADGE = { invite: "badge-muted", inscrit: "badge-warn", abonne: "badge-valid" };

export default function Parrainage() {
  const [data, setData] = useState(null);
  const [emailInvite, setEmailInvite] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/parrainage/mon-code");
      setData(data);
    } catch (err) { toast.error(fmtError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = async (txt, label) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(`${label} copié`);
    } catch { toast.error("Copie impossible"); }
  };

  const inviter = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/parrainage/inviter", { email: emailInvite });
      toast.success("Invitation enregistrée. Partagez le lien avec votre filleul.");
      setEmailInvite("");
      load();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  if (!data) return <div className="p-4">Chargement…</div>;

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933] flex items-center gap-2">
        <Gift size={24} className="text-[#E8710A]" /> Parrainage
      </h1>
      <p className="text-sm text-[#3E4C59] mt-1">Parrainez d'autres entreprises et gagnez 1 mois offert par filleul abonné.</p>

      <div className="card p-4 mt-4" data-testid="parrainage-code-card">
        <div className="grid md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-wider text-[#3E4C59]">Votre code de parrainage</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-2xl font-bold text-[#1B3A5C]" data-testid="parrainage-code">{data.code}</span>
              <button onClick={() => copy(data.code, "Code")} className="btn-outline text-xs" data-testid="copy-code"><Copy size={12} /> Copier</button>
            </div>
            <div className="mt-3 text-xs uppercase tracking-wider text-[#3E4C59]">Lien d'invitation</div>
            <div className="flex items-center gap-2 mt-1">
              <code className="text-xs bg-[#F5F6F7] px-2 py-1 rounded-[4px] flex-1 truncate" data-testid="parrainage-link">{data.lien_invitation}</code>
              <button onClick={() => copy(data.lien_invitation, "Lien")} className="btn-outline text-xs" data-testid="copy-link"><Copy size={12} /> Copier</button>
            </div>
          </div>
          <div className="card p-3 bg-[#F5F6F7] border-[#E4E7EB]">
            <div className="text-xs uppercase tracking-wider text-[#3E4C59]">Mois offerts acquis</div>
            <div className="text-3xl font-bold text-[#1B3A5C]" data-testid="mois-acquis">{data.mois_offerts_annee_courante}<span className="text-base text-[#3E4C59]"> / {data.plafond_annuel}</span></div>
            <div className="text-xs text-[#3E4C59] mt-1">Sur l'année en cours. Plafond {data.plafond_annuel} mois / an.</div>
            <div className="text-xs text-[#3E4C59] mt-1">Cumulé depuis l'inscription : {data.mois_offerts_total}</div>
          </div>
        </div>
      </div>

      <div className="card p-4 mt-4 border-l-4 border-[#1B3A5C]" data-testid="parrainage-note">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-[#1B3A5C] mt-0.5" />
          <p className="text-sm text-[#3E4C59]">
            Le crédit d'un mois est <strong>enregistré à l'inscription</strong> mais <strong>appliqué uniquement</strong> une fois la souscription effective de votre filleul. Le paiement en ligne n'étant pas encore branché, ces crédits restent décoratifs jusqu'au lancement de la facturation.
          </p>
        </div>
      </div>

      <form onSubmit={inviter} className="card p-4 mt-4" data-testid="invite-form">
        <div className="text-sm font-semibold text-[#1F2933]">Inviter par e-mail</div>
        <div className="flex flex-wrap items-end gap-2 mt-2">
          <div className="flex-1 min-w-[240px]">
            <label className="label">E-mail du filleul</label>
            <input type="email" required className="input" value={emailInvite} onChange={(e) => setEmailInvite(e.target.value)} data-testid="invite-email" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="invite-submit">
            <Send size={14} /> {busy ? "…" : "Enregistrer l'invitation"}
          </button>
        </div>
        <p className="text-xs text-[#3E4C59] mt-2">L'invitation ne déclenche pas d'e-mail automatique (SMTP à venir). Partagez le lien manuellement pour l'instant.</p>
      </form>

      <div className="card mt-4 overflow-x-auto" data-testid="filleuls-list">
        <div className="p-3 border-b border-[#E4E7EB]">
          <h2 className="text-lg font-semibold text-[#1F2933]">Filleuls</h2>
        </div>
        <table className="table-compact w-full">
          <thead>
            <tr><th>E-mail</th><th>SIREN</th><th>Raison sociale</th><th>Date</th><th>Statut</th><th>Mois offert</th></tr>
          </thead>
          <tbody>
            {data.filleuls.length === 0 && <tr><td colSpan={6} className="text-center text-[#3E4C59]">Aucun filleul pour l'instant.</td></tr>}
            {data.filleuls.map(f => (
              <tr key={f.id} data-testid={`filleul-row-${f.id}`}>
                <td>{f.filleul_email}</td>
                <td className="font-mono text-xs">{f.filleul_siren || <span className="text-[#3E4C59]">—</span>}</td>
                <td>{f.filleul_raison_sociale || <span className="text-[#3E4C59]">—</span>}</td>
                <td>{fmtDate(f.created_at)}</td>
                <td><span className={STATUT_BADGE[f.statut] || "badge-muted"}>{STATUT_LABEL[f.statut] || f.statut}</span></td>
                <td>
                  {f.annulle_par_admin ? <span className="badge-danger" title={f.motif_annulation || ""}>Annulé</span>
                    : f.mois_offert_credite_parrain ? <span className="badge-valid">Crédité</span>
                    : <span className="text-[#3E4C59] text-xs">En attente d'abonnement</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
